import {
  isAgentBusy,
  isTerminalInvestigationStatus,
} from "#src/view-models/investigations";
import {
  type ApprovalDecisionType,
  InvestigationAdapter,
  InvestigationAdapterQueryKeys,
  Resources,
  type CreateInvestigationBodyType,
  type CreateInvestigationMessageBodyType,
  type InvestigationDetailType,
} from "@validereinc/domain";
import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { useToast } from "@validereinc/common-components";
import { useState } from "react";

const POLLING_INTERVAL_MS = 2_500;
const DEFAULT_PAGE_SIZE = 20;
const DEFAULT_MESSAGES_PAGE_SIZE = 20;

type SubmitInvestigationApprovalBodyType = {
  decision: ApprovalDecisionType;
  content?: string;
};

const useInvalidateInvestigationDetailAndMessages = (id: string) => {
  const queryClient = useQueryClient();
  const [isInvalidating, setIsInvalidating] = useState(false);

  const invalidateDetailAndMessages = () =>
    Promise.all([
      queryClient.invalidateQueries({
        queryKey: InvestigationAdapterQueryKeys.getOne({ id }),
      }),
      queryClient.invalidateQueries({
        queryKey: [Resources.INVESTIGATION, "messages", "getList", id],
      }),
    ]);

  const invalidateDetailAndMessagesWithLoading = async () => {
    setIsInvalidating(true);
    try {
      await invalidateDetailAndMessages();
    } finally {
      setIsInvalidating(false);
    }
  };

  return {
    invalidateDetailAndMessages,
    invalidateDetailAndMessagesWithLoading,
    isInvalidating,
  };
};

/**
 * Detect the 409 the API returns when a follow-up arrives while the agent is
 * mid-turn. We don't have a typed error class from the rest client yet, so we
 * sniff the shape: `{ response: { status: 409, data: { code: "investigation_in_progress" } } }`.
 */
export const isInvestigationInProgressError = (err: unknown): boolean => {
  if (typeof err !== "object" || err === null) return false;
  const response = (err as { response?: unknown }).response;
  if (typeof response !== "object" || response === null) return false;
  const { status, data } = response as { status?: unknown; data?: unknown };
  if (status !== 409) return false;
  if (typeof data !== "object" || data === null) return true;
  const code = (data as { code?: unknown }).code;
  return code === undefined || code === "investigation_in_progress";
};

type InvestigationMessageMutationToastType = ReturnType<
  typeof useToast
>["toast"];

/**
 * Shared `onError` for investigation `messages.createOne` mutations (follow-up
 * and approval). On 409 `investigation_in_progress`, show a warning and refresh
 * stale detail/messages cache so the next poll converges.
 */
const createInvestigationMessageMutationOnError =
  (
    invalidateDetailAndMessages: () => Promise<unknown[]>,
    toast: InvestigationMessageMutationToastType,
    genericFailureDescription: string
  ) =>
  (err: unknown) => {
    const isInProgress = isInvestigationInProgressError(err);

    toast.push({
      intent: isInProgress ? "warning" : "error",
      description: isInProgress
        ? "An investigation turn is still running. Please try again shortly."
        : genericFailureDescription,
    });

    if (isInProgress) {
      void invalidateDetailAndMessages();
    }

    console.error(err);
  };

export const useInvestigation = (id?: string) => {
  const queryClient = useQueryClient();

  return useQuery<InvestigationDetailType>({
    queryKey: InvestigationAdapterQueryKeys.getOne({ id: id ?? "" }),
    queryFn: async () => {
      const { data } = await InvestigationAdapter.getOne({ id: id! });
      // The cache still holds the previous detail at this point; react-query
      // only writes `data` after `queryFn` resolves. If we observe a busy →
      // terminal transition here, kick a one-shot messages refetch so the
      // final agent reply shows up without a manual reload (the messages
      // poll is about to stop on the very next tick).
      const previous = queryClient.getQueryData<InvestigationDetailType>(
        InvestigationAdapterQueryKeys.getOne({ id: id! })
      );
      if (
        previous &&
        isAgentBusy(previous.status) &&
        isTerminalInvestigationStatus(data.status)
      ) {
        void queryClient.invalidateQueries({
          queryKey: [Resources.INVESTIGATION, "messages", "getList", id!],
        });
      }
      return data;
    },
    enabled: Boolean(id),
    refetchInterval: (data) =>
      data && !isTerminalInvestigationStatus(data.status)
        ? POLLING_INTERVAL_MS
        : false,
  });
};

export const useInvestigationsInfiniteList = ({
  pageSize = DEFAULT_PAGE_SIZE,
}: { pageSize?: number } = {}) =>
  useInfiniteQuery({
    queryKey: [
      ...InvestigationAdapterQueryKeys.getList({ pageSize }),
      "infinite",
    ] as const,
    queryFn: async ({ pageParam = 1 }) =>
      InvestigationAdapter.getList({
        page: pageParam as number,
        pageSize,
      }),
    getNextPageParam: (lastPage, allPages) => {
      const fetched = allPages.reduce((sum, p) => sum + p.data.length, 0);
      return fetched < lastPage.total_entries ? allPages.length + 1 : undefined;
    },
  });

export const useInvestigationMessages = (
  id: string | undefined,
  { pageSize = DEFAULT_MESSAGES_PAGE_SIZE }: { pageSize?: number } = {}
) => {
  const queryClient = useQueryClient();

  return useInfiniteQuery({
    queryKey: InvestigationAdapterQueryKeys.messages.getList({
      meta: { id: id ?? "" },
      pageSize,
    }),
    queryFn: ({ pageParam = 1 }) =>
      InvestigationAdapter.messages.getList({
        meta: { id: id! },
        page: pageParam as number,
        pageSize,
      }),
    enabled: Boolean(id),
    // Poll in lock-step with `useInvestigation` so agent replies surface without
    // a manual refresh. Read the detail query directly from the cache because
    // the `refetchInterval` callback only sees the messages query's own data.
    refetchInterval: () => {
      if (!id) return false;
      const detail = queryClient.getQueryData<InvestigationDetailType>(
        InvestigationAdapterQueryKeys.getOne({ id })
      );
      return detail && isTerminalInvestigationStatus(detail.status)
        ? false
        : POLLING_INTERVAL_MS;
    },
    getNextPageParam: (lastPage, allPages) => {
      const fetched = allPages.reduce((sum, p) => sum + p.data.length, 0);
      return fetched < lastPage.total_entries ? allPages.length + 1 : undefined;
    },
  });
};

export const useCreateInvestigation = () => {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (body: CreateInvestigationBodyType) =>
      InvestigationAdapter.createOne({ data: body }),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [Resources.INVESTIGATION],
      });
    },
    onError: (err) => {
      toast.push({
        intent: "error",
        description: "Failed to start the investigation. Please try again.",
      });
      console.error(err);
    },
  });
};

export const usePostInvestigationMessage = (id: string) => {
  const { toast } = useToast();
  const {
    invalidateDetailAndMessages,
    invalidateDetailAndMessagesWithLoading,
    isInvalidating,
  } = useInvalidateInvestigationDetailAndMessages(id);

  // On a 409 race the cache is stale: the detail page still thinks the agent
  // is idle. Invalidate both the detail and the message list so the next poll
  // pulls the fresh status and the now-finished agent reply.
  const mutation = useMutation({
    mutationFn: (body: CreateInvestigationMessageBodyType) =>
      InvestigationAdapter.messages.createOne({
        data: body,
        meta: { id },
      }),
    onSuccess: invalidateDetailAndMessagesWithLoading,
    onError: createInvestigationMessageMutationOnError(
      invalidateDetailAndMessages,
      toast,
      "Failed to send the follow-up. Please try again."
    ),
  });

  return {
    ...mutation,
    isLoading: mutation.isLoading || isInvalidating,
  };
};

export const useSubmitInvestigationApproval = (id: string) => {
  const { toast } = useToast();
  const {
    invalidateDetailAndMessages,
    invalidateDetailAndMessagesWithLoading,
    isInvalidating,
  } = useInvalidateInvestigationDetailAndMessages(id);

  const mutation = useMutation({
    mutationFn: (body: SubmitInvestigationApprovalBodyType) => {
      const messageBody: CreateInvestigationMessageBodyType = {
        kind: "approval",
        decision: body.decision,
      };
      if (body.content) {
        messageBody.content = body.content;
      }

      return InvestigationAdapter.messages.createOne({
        data: messageBody,
        meta: { id },
      });
    },
    onSuccess: invalidateDetailAndMessagesWithLoading,
    onError: createInvestigationMessageMutationOnError(
      invalidateDetailAndMessages,
      toast,
      "Failed to submit approval. Please try again."
    ),
  });

  return {
    ...mutation,
    isLoading: mutation.isLoading || isInvalidating,
  };
};
